import type { DeliveryMethodDto } from '../../generated-api/models/DeliveryMethodDto';
import type { DeliveryInfo } from '../../types/payment';
import { attemptToGetHeartBeat } from './cash';
import { captureException, captureMessage } from '@sentry/react';
import { tryCatchAsyncWrapper } from './generic';

export const validateDeliveryInfo = (deliveryInfo: DeliveryInfo): Record<string, 'valid' | 'invalid'> => {
  if (!deliveryInfo.deliveryMethod) {
    return {};
  }
  return {
    primaryReference: deliveryInfo.primaryReference ? 'valid' : 'invalid',
    secondaryReference:
      deliveryInfo.secondaryReference || !deliveryInfo.deliveryMethod.secondaryReferenceEnabled ? 'valid' : 'invalid',
    tertiaryReference:
      deliveryInfo.tertiaryReference || !deliveryInfo.deliveryMethod.tertiaryReferenceEnabled ? 'valid' : 'invalid',
  };
};

export const isValid = (validationRecord: Record<string, 'valid' | 'invalid'>) =>
  Object.values(validationRecord).every((value) => value === 'valid');

export const isDisplayedDeliveryMethod = (method: DeliveryMethodDto) => method.type !== 'PICKUP';

export const sendIosMessageCashPaymentSuccess = async (
  sum: number,
  paymentReference: string,
  locationId: string,
  currency: string,
  onError?: (e: Error) => void,
  throwError?: boolean
) => {
  if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.cashPaymentHandler) {
    if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.heartbeat) {
      const [data, error] = await tryCatchAsyncWrapper(() => attemptToGetHeartBeat(paymentReference, locationId));
      if (error) {
        captureException(error);
        onError && onError(error);
        if (throwError) {
          throw error;
        }
      }

      if (data !== 'alive') {
        captureMessage(
          `Payment ${paymentReference} failed to get a heartbeat on location ${locationId}, skipping heartbeat and sending to payment.`
        );
        const customError = new Error(
          `Unable to connect to the cash drawer, no sale will be registered in reports for ${paymentReference}. The current bill will be closed, please create a new bill and attempt again. `
        );
        onError && onError(customError);
        if (throwError) {
          throw customError;
        }
      }
      // oxlint-disable require-post-message-target-origin -- webkit messageHandler, not window.postMessage
      window.webkit.messageHandlers.cashPaymentHandler.postMessage({
        currency,
        locationId,
        paymentReference,
        sum,
      });
      // oxlint-enable require-post-message-target-origin
      return;
    }
    // oxlint-disable require-post-message-target-origin -- webkit messageHandler, not window.postMessage
    window.webkit.messageHandlers.cashPaymentHandler.postMessage({
      currency,
      locationId,
      paymentReference,
      sum,
    });
    // oxlint-enable require-post-message-target-origin
    return;
  }
  captureMessage(
    `No Messages buses found for ${paymentReference} on location ${locationId}, Cash reconsiliation will fail.`
  );
};
